import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {businessesApi} from 'src/api';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {InvoiceStatusEnum} from 'src/enums';
import {useCan} from 'src/permissions/hooks';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {InvoicesTableActionsType} from './InvoicesTableActions.types';

const ALL = '';

/**
 * The invoices toolbar.
 *
 * Status everywhere, business only on the revenue list — that is the one invoice list whose rows span
 * businesses. Elsewhere the page is already about one, chosen in the sidebar, and a filter that could
 * change it behind the sidebar's back was a way to read a list that disagreed with its own heading.
 */
export const InvoicesTableActions: FC<InvoicesTableActionsType> = ({
                                                                      handleSearch,
                                                                      handleFilter,
                                                                      revenue = false,
                                                                      title,
                                                                      icon,
                                                                      onAdd,
                                                                      addLabel,
                                                                      totalElements,
                                                                      status,
                                                                      businessId,
                                                                      searchValue,
                                                                  }) => {
    const intl = useIntl();
    // Both read from the filter the page holds in the address bar. They used to be `useState` here,
    // which is a second copy of the same two values: the strip above sets the status as well, and the
    // copy that had not heard about it won the next time anything else in the toolbar changed.
    const business = businessId ? String(businessId) : ALL;

    // `revenue` is which table this is, not who is looking; the business selector itself is the
    // platform-wide read that GET /businesses requires. Asked unconditionally: a hook behind a `&&`
    // runs on some renders and not others, which is what breaks React.
    const canReadPlatformBusinesses = useCan('businesses.readPlatform');
    const canPickBusiness = revenue && canReadPlatformBusinesses;
    const [getBusinesses, businesses] = businessesApi.useLazyGetBusinessesQuery();

    useEffect(() => {
        if (canPickBusiness) {
            void getBusinesses({});
        }
    }, []);

    const apply = (next: {status?: string; business?: string}) => {
        const value = {status, business, ...next};
        handleFilter({
            status: value.status as InvoiceStatusEnum | string,
            businessId: Number(value.business) || 0,
        });
    };

    const statusOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allStatuses'}), value: ALL},
        // The enum's own names, as the column shows them.
        ...Object.values(InvoiceStatusEnum).map((value, index) => ({id: index + 1, name: value, value})),
    ], [intl]);

    const businessOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allBusinesses'}), value: ALL},
        ...(businesses.data?.content ?? []).map((item) => ({
            id: item.id,
            name: item.name,
            value: String(item.id),
        })),
    ], [businesses.data?.content, intl]);

    const fields: FilterBarField[] = [
        {
            id: 'status',
            control: (
                <AMPFormSelect
                    dense
                    data={statusOptions}
                    value={status}
                    input="status"
                    onChange={(e) => apply({status: String(e.target.value)})}
                />
            ),
        },
        ...(canPickBusiness ? [{
            id: 'business',
            wide: true,
            control: (
                <AMPFormSelect
                    dense
                    data={businessOptions}
                    value={business}
                    input="business"
                    onChange={(e) => apply({business: String(e.target.value)})}
                />
            ),
        }] : []),
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            fields={fields}
            hasActiveFilters={Boolean(status || business)}
            onClearAll={() => apply({status: ALL, business: ALL})}
            actions={(
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    placeholder={intl.formatMessage({id: 'search'})}
                    name="search"
                    // Uncontrolled, so that the debounce is not fighting a re-render on every
                    // keystroke - but seeded, so the box still says what the list is filtered by
                    // after a Back.
                    defaultValue={searchValue ?? ''}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                        debounce(() => handleSearch(event.target.value), 1000)()}
                    startAdornment={<Search/>}
                />
            )}
        />
    );
};
